/**
 * Browser-side image optimizer for admin uploads.
 *
 * Why this exists: sponsors and operators upload logos / banners / hero
 * shots straight off a phone or a designer's machine — often 3000-6000px
 * wide and several MB. Every passenger and driver in Dominica then
 * downloads that full-resolution file (the first time they see the ad),
 * which is wasteful on prepaid mobile data and slow on weak connections.
 *
 * A phone can only display a few hundred to ~1920 device pixels for these
 * slots, so we downscale to a generous cap that stays pixel-sharp on the
 * highest-density phones, then re-encode. This is the same "resize on
 * ingest" approach Uber / Instagram / Airbnb use — visually identical on a
 * phone, a fraction of the bytes.
 *
 * Quality safeguards:
 *  - Never upscales (a small source is left exactly as-is).
 *  - Photos -> WebP at high quality (visually indistinguishable, ~70-90%
 *    smaller). Logos -> downscaled PNG (lossless, keeps crisp edges + alpha).
 *  - Vector (SVG) and animated (GIF) formats are skipped untouched.
 *  - If the browser can't encode or the result isn't smaller, the ORIGINAL
 *    file is returned — so this can never make an upload worse or break it.
 */

export type OptimizeFormat = 'auto' | 'webp' | 'png';

export interface OptimizeOptions {
  /** Cap on the longest side, in pixels. */
  maxDimension?: number;
  /** Lossy quality for WebP/JPEG output (0..1). Ignored for PNG. */
  quality?: number;
  /**
   * Output format. `auto` (default) re-encodes photos as WebP. `png` keeps
   * the image lossless (best for logos / line art with text or transparency).
   */
  format?: OptimizeFormat;
}

/** Formats we must never rasterize/resize — would destroy them. */
const SKIP_TYPES = new Set(['image/svg+xml', 'image/gif']);

/**
 * Downscale + re-encode an image File for upload. Always resolves to a File;
 * on any uncertainty it resolves to the original so callers never break.
 */
export async function optimizeImageForUpload(
  file: File,
  opts: OptimizeOptions = {},
): Promise<File> {
  const maxDimension = opts.maxDimension ?? 1920;
  const quality = opts.quality ?? 0.85;
  const format = opts.format ?? 'auto';

  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/svg+xml' && format === 'png') {
    return rasterizeSvgToPng(file, Math.min(maxDimension, 512));
  }
  if (SKIP_TYPES.has(file.type)) return file;

  // Decode. createImageBitmap is supported in every browser our admins use;
  // if it throws (corrupt/unsupported), fall back to the original file.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const { width, height } = bitmap;
    if (!width || !height) return file;

    const longest = Math.max(width, height);
    const scale = longest > maxDimension ? maxDimension / longest : 1;
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // High-quality downscaling.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const outType = format === 'png' ? 'image/png' : 'image/webp';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outType, format === 'png' ? undefined : quality),
    );

    // Encoder unsupported (older Safari + WebP) or failed → keep original.
    if (!blob || !blob.type) return file;

    // If we didn't shrink AND the re-encode isn't smaller, the original wins.
    // Exception: a PNG preset keeps the PNG. Logos end up inside SVG map pins
    // on Android, which draw JPEG/WebP logos as a blank circle.
    const mustConvert = format === 'png' && file.type !== 'image/png';
    if (scale === 1 && blob.size >= file.size && !mustConvert) return file;

    const ext = blob.type === 'image/webp' ? 'webp' : blob.type === 'image/png' ? 'png' : 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.${ext}`, {
      type: blob.type,
      lastModified: Date.now(),
    });
  } finally {
    bitmap.close?.();
  }
}

/**
 * Draw an SVG logo onto a canvas and export a PNG, longest side `size` px.
 * Resolves to the original file if the browser can't load the SVG.
 */
async function rasterizeSvgToPng(file: File, size: number): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('svg load failed'));
      el.src = url;
    });
    const w = img.naturalWidth || size;
    const h = img.naturalHeight || size;
    const scale = size / Math.max(w, h);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return file;
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'logo';
    return new File([blob], `${baseName}.png`, { type: 'image/png', lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Tuned preset for full-bleed photos / hero / campaign creatives. */
export const PHOTO_PRESET: OptimizeOptions = {
  maxDimension: 1920,
  quality: 0.85,
  format: 'auto',
};

/** Tuned preset for logos / brand marks: lossless, smaller cap, keeps alpha. */
export const LOGO_PRESET: OptimizeOptions = {
  maxDimension: 800,
  format: 'png',
};
